import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import { generateNickname } from '../utils/nicknameGenerator';

export interface UserProfile {
  uid: string;
  email: string;
  nickname: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  emailPreferences?: {
    statusUpdates: boolean;
    newComments: boolean;
    weeklyDigest: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch or create user profile
  const fetchOrCreateUserProfile = async (firebaseUser: User): Promise<UserProfile | null> => {
    try {
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User profile exists, return it
        return userDoc.data() as UserProfile;
      } else {
        // Create new user profile with auto-generated nickname
        const newProfile: UserProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || 'anonymous@example.com',
          nickname: generateNickname(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          emailPreferences: {
            statusUpdates: true,
            newComments: true,
            weeklyDigest: true,
          },
        };

        await setDoc(userDocRef, newProfile);
        console.log('Created new user profile with nickname:', newProfile.nickname);
        return newProfile;
      }
    } catch (error) {
      console.error('Error fetching/creating user profile:', error);
      return null;
    }
  };

  const refreshUserProfile = async () => {
    if (!user) return;
    const profile = await fetchOrCreateUserProfile(user);
    setUserProfile(profile);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // User is logged in, fetch or create their profile
        const profile = await fetchOrCreateUserProfile(firebaseUser);
        setUserProfile(profile);
      } else {
        // User is logged out
        setUserProfile(null);
      }

      setLoading(false);
      console.log('Auth state changed:', firebaseUser?.email || 'Not logged in');
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUserProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, signOut, refreshUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
